import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CURRENT_YEAR = new Date().getFullYear();

const Payees = () => {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy]   = useState('total');   // 'name' | 'payments' | 'total'
  const [sortDir, setSortDir] = useState('desc');    // 'asc' | 'desc'
  // Defaults to the calendar year — the range that matters for 1099 prep.
  const [fromDate, setFromDate] = useState(`${CURRENT_YEAR}-01-01`);
  const [toDate, setToDate]     = useState(`${CURRENT_YEAR}-12-31`);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'transactions'), snap => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [orgId]);

  // Grouped by the exact `payableTo` string on each transaction (splits carry
  // payableTo at the top level too, so no need to look inside `lines`).
  const byPayee = {};
  transactions.forEach(t => {
    if (t.type === 'Starting Balance' || !t.payableTo) return;
    if ((fromDate && t.date < fromDate) || (toDate && t.date > toDate)) return;
    const name = t.payableTo;
    if (!byPayee[name]) byPayee[name] = { total: 0, count: 0, lastDate: '' };
    byPayee[name].total += t.expense || 0;
    byPayee[name].count += 1;
    if (t.date > byPayee[name].lastDate) byPayee[name].lastDate = t.date;
  });

  const rows = Object.entries(byPayee)
    .map(([name, v]) => ({ name, ...v }))
    .filter(r => !search.trim() || r.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      let cmp;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortBy === 'payments') cmp = a.count - b.count;
      else cmp = a.total - b.total;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const toggleSort = (col, defaultDir) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(defaultDir); }
  };

  const sortArrow = (col) => sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  const openInTransactions = (name) => {
    const params = new URLSearchParams({ payee: name });
    if (fromDate) params.set('from', fromDate);
    if (toDate)   params.set('to', toDate);
    navigate(`/transactions?${params.toString()}`);
  };

  const setYear = (y) => { setFromDate(`${y}-01-01`); setToDate(`${y}-12-31`); };
  const clearDates = () => { setFromDate(''); setToDate(''); };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Payees</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Total paid to each payee{fromDate || toDate ? ` — ${fromDate || 'start'} to ${toDate || 'today'}` : ', all time'}
          </p>
        </div>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search payees…"
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white w-56 focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <label className="text-xs font-semibold text-slate-600">From</label>
        <input
          type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <label className="text-xs font-semibold text-slate-600">To</label>
        <input
          type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <button onClick={() => setYear(CURRENT_YEAR)} className="text-xs text-blue-600 hover:underline ml-1">
          This year
        </button>
        <button onClick={() => setYear(CURRENT_YEAR - 1)} className="text-xs text-blue-600 hover:underline">
          Last year
        </button>
        <button onClick={clearDates} className="text-xs text-blue-600 hover:underline">
          All time
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-500 text-sm">
          {search ? `No payees match "${search}".` : 'No payments recorded yet.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-200 text-slate-600">
                <th className="px-4 py-2.5 text-left font-semibold">
                  <button onClick={() => toggleSort('name', 'asc')} className="hover:text-slate-900">
                    Payee{sortArrow('name')}
                  </button>
                </th>
                <th className="px-4 py-2.5 text-right font-semibold">
                  <button onClick={() => toggleSort('payments', 'desc')} className="hover:text-slate-900">
                    Payments{sortArrow('payments')}
                  </button>
                </th>
                <th className="px-4 py-2.5 text-right font-semibold">Total Paid</th>
                <th className="px-4 py-2.5 text-left font-semibold">Last Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.name} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => openInTransactions(r.name)}
                      className="text-blue-600 hover:underline font-medium text-left"
                      title={`View ${r.name}'s transactions`}
                    >
                      {r.name}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{r.count}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-red-600">{fmt(r.total)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.lastDate}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-200">
                <td className="px-4 py-2.5">Total ({rows.length} payees)</td>
                <td className="px-4 py-2.5 text-right">{rows.reduce((s, r) => s + r.count, 0)}</td>
                <td className="px-4 py-2.5 text-right text-red-600">{fmt(grandTotal)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default Payees;
